import { DEFER_COLLAB_SEND } from '@atlaskit/editor-common/collab-defer-collab-send';
import { logException } from '@atlaskit/editor-common/monitoring/error';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import type { FireAnalyticsStep } from '../../../ui/utils/analytics/analyticsFlowTypes';
import { aiSessionContextPluginKey } from '../../ai-session-context/ai-session-context-plugin-key';
import { STREAMING_COMMIT_RESULT } from '../../ai-session-context/meta-keys';
import { commitAttributes, type CommitOutcome } from '../commitOutcome';
import { purePmInEditorDirectStreamingPluginKey } from '../pluginKey';
import { buildCommitTransaction } from '../transaction-builders/buildCommitTransaction';
import { buildRestoreTransaction } from '../transaction-builders/buildRestoreTransaction';
import { updateStreamPositions } from '../transforms';
import type { PurePmInEditorDirectStreamingPluginState } from '../types';
import { clampPosition } from '../utils/positionValidation';

function finishWithoutContent(
	state: EditorState,
	previousStream: PurePmInEditorDirectStreamingPluginState,
): Transaction {
	const stream = purePmInEditorDirectStreamingPluginKey.getState(state) ?? previousStream;
	const end = clampPosition(stream.aiContentEndPos ?? stream.startPos ?? 0, state.doc.content.size);
	return updateStreamPositions({
		aiContentEndPos: end,
		telepointerPos: end,
		isFinalStream: true,
	})(state.tr.setMeta('addToHistory', false).setMeta(DEFER_COLLAB_SEND, false));
}

/**
 * Mutable state container for finalize handling.
 * Tracks re-entry guard during the two-dispatch pattern.
 */
export interface FinalizeViewState {
	isFinalizingInProgress: boolean;
}

/**
 * Creates the initial finalize view state.
 */
export function createFinalizeViewState(): FinalizeViewState {
	return {
		isFinalizingInProgress: false,
	};
}

/**
 * Handles finalizing streamed content using a two-dispatch pattern.
 *
 * This uses TWO separate dispatches to work correctly with track-changes:
 * 1. DISPATCH 1: Restore original content (streaming → original)
 * 2. DISPATCH 2: Commit final content (original → final)
 *
 * The two-dispatch pattern is critical for track-changes compatibility.
 *
 * @param view - The ProseMirror editor view
 * @param state - Current streaming plugin state
 * @param finalizeState - Mutable state container for finalize tracking
 * @param fireAnalyticsStep - Callback to fire the 'complete' analytics step (no target = finished)
 */
export function handleFinalize(
	view: EditorView,
	state: PurePmInEditorDirectStreamingPluginState,
	finalizeState: FinalizeViewState,
	fireAnalyticsStep?: FireAnalyticsStep,
): void {
	// Guard: Don't re-enter while we're dispatching restore/commit
	if (finalizeState.isFinalizingInProgress) {
		return;
	}

	const sessionState = aiSessionContextPluginKey.getState(view.state);
	if (!sessionState) {
		return;
	}

	// Check if finalize is needed:
	// - Session status is 'complete'
	// - Streaming state is in 'streaming' phase and actively streaming
	// - Final stream hasn't been applied yet
	const needsFinalize =
		sessionState.status === 'complete' &&
		state.streamPhase === 'streaming' &&
		state.isStreaming &&
		!state.hasFinalStream;

	if (!needsFinalize) {
		return;
	}

	// Set flag to prevent re-entry during dispatches
	finalizeState.isFinalizingInProgress = true;
	const observeCommit = isExperimentEnabled('platform_editor_ai_commit_rejection');
	const actionId = sessionState.commitActionId;

	try {
		// DISPATCH 1: Restore original content (streaming → original)
		const restoreTr = buildRestoreTransaction(view.state);
		if (restoreTr) {
			view.dispatch(restoreTr);
		}

		// DISPATCH 2: Commit final content (original → final)
		// `buildCommitTransaction` can return a state-only transaction that marks the
		// stream finished without ever putting the AI content in the document — most
		// notably when the final fragment fails the editor schema. That path is
		// indistinguishable from success in the funnel because 'complete' still fires
		// below, so record why the content was dropped.
		let commitOutcome: CommitOutcome | undefined;
		let commitTr = buildCommitTransaction(view.state, (outcome) => {
			commitOutcome = outcome;
		});
		if (observeCommit && commitOutcome) {
			if (!commitTr && !commitOutcome.applied) {
				commitTr = finishWithoutContent(view.state, state);
			}
			if (commitTr && actionId !== undefined) {
				commitTr.setMeta(STREAMING_COMMIT_RESULT, { actionId, outcome: commitOutcome });
			}
		}
		if (commitTr) {
			view.dispatch(commitTr);
		}
		// Fire the 'complete' step with no target so that analyticsFlow sets finished=true
		// and dispatches the 'finished' userFlow event. This is the terminal step in the
		// xstate migration path — the loading step (fired in buildUpdateTransaction) transitions
		// to 'complete', and 'complete' with no target signals end-of-flow.
		fireAnalyticsStep?.('complete', undefined, commitAttributes(commitOutcome));
	} catch (error) {
		logException(error instanceof Error ? error : new Error(String(error)), {
			location: 'editor-ai-streaming-finalize',
		});
		// A callback or dispatch can throw after the commit has already landed.
		// Preserve an observed terminal result rather than replacing it with failure.
		const committed =
			purePmInEditorDirectStreamingPluginKey.getState(view.state)?.hasFinalStream ||
			(actionId !== undefined &&
				aiSessionContextPluginKey.getState(view.state)?.commitResult?.actionId === actionId);
		if (observeCommit && !committed) {
			const failedTr = finishWithoutContent(view.state, state);
			if (actionId !== undefined) {
				failedTr.setMeta(STREAMING_COMMIT_RESULT, {
					actionId,
					outcome: { applied: false, reason: 'finalizeThrew' },
				});
			}
			try {
				view.dispatch(failedTr);
			} catch (completionError) {
				logException(
					completionError instanceof Error ? completionError : new Error(String(completionError)),
					{ location: 'editor-ai-streaming-finalize' },
				);
			}
		}
		// Retain the operational exception report and failed user-flow, including
		// exceptions raised after the commit has already landed.
		fireAnalyticsStep?.('failed', undefined, {
			errorKey: 'finalizeThrew',
			errorType: 'contentNotApplied',
		});
	} finally {
		finalizeState.isFinalizingInProgress = false;
	}
}
